import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useEffect } from "react";
import type { MembershipResponse } from "../types";
import type { AxiosResult } from "@/lib/axios";

const inviteSchema = z.object({
	email: z.string().email("Please enter valid email address"),
	role: z.literal("editor").default("editor"),
});

type InviteFormData = z.infer<typeof inviteSchema>;

interface CreateInviteFormProps {
	onSubmit: (data: InviteFormData) => Promise<AxiosResult<MembershipResponse | null> | null>;
	isLoading?: boolean;
	error?: string | null;
	onErrorChange?: (error: string | null) => void;
	disabled?: boolean;
}

export default function CreateInviteForm({ onSubmit, isLoading = false, error, onErrorChange, disabled = false }: CreateInviteFormProps) {
	const roleStyles = {
		editor: "bg-blue-50 text-blue-700 border-blue-200",
	} as const;
	const currentRoleStyle = roleStyles.editor;
	const formDisabled = isLoading || disabled;

	const {
		register,
		handleSubmit,
		formState: { errors },
		reset,
		clearErrors,
	} = useForm<InviteFormData>({
		resolver: zodResolver(inviteSchema) as any,
		defaultValues: {
			email: "",
			role: "editor",
		},
		// mode: "onSubmit",
		// reValidateMode: "onSubmit",
	});

	// Clear local email validation error when parent error is present
	useEffect(() => {
		if (error) {
			clearErrors("email");
		}
	}, [error, clearErrors]);

	// Clear parent error when local email validation error occurs
	useEffect(() => {
		if (errors.email && error && onErrorChange) {
			onErrorChange(null);
		}
	}, [errors.email, error, onErrorChange]);

	const submitHandler = async (data: InviteFormData) => {
		const result = await onSubmit(data);
		if (result?.success) {
			// Success - clear the form
			reset();
		}
	};

	return (
		<form onSubmit={handleSubmit(submitHandler)}>
			<div className="flex items-center gap-3">
				<div className="flex-1">
					<input
						id="email"
						type="text"
						placeholder="Enter email address to invite"
						className="w-full px-4 py-3 text-sm text-gray-900 bg-gray-50 border border-gray-200 rounded-xl outline-none placeholder:text-gray-400 focus:border-brand focus:ring-1 focus:ring-brand transition-colors"
						disabled={formDisabled}
						{...register("email")}
					/>
				</div>

				<span className={`inline-flex items-center justify-center px-3 py-3 text-xs font-medium rounded-xl border ${currentRoleStyle}`}>Editor</span>

				<button
					type="submit"
					disabled={formDisabled}
					className="px-5 py-3 rounded-xl bg-brand text-white font-medium text-sm hover:bg-brand/90 focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-1 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap transition-colors"
				>
					{isLoading ? "Sending..." : "Send Invite"}
				</button>
			</div>

			{(error || errors?.email) && <p className="text-sm text-red-500 mt-2">{error || errors.email?.message}</p>}

			<p className="text-xs text-gray-400 mt-2">{disabled ? "Team invites are disabled on your current plan." : "There is no additional charge for inviting team members."}</p>
		</form>
	);
}
